import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { ApiEnvelope } from './super-admin-auth.service';

export interface OrganizationRecord {
  id: number;
  organization_name: string;
  legal_name: string | null;
  gst_no: string | null;
  pan_no: string | null;
  country_code: string | null;
  mobile_number: string | null;
  email: string | null;
  address: string | null;
  city: string | null;
  country: string | null;
  pincode: string | null;
  logo_url: string | null;
  bank_name: string | null;
  bank_account_name: string | null;
  bank_account_number: string | null;
  bank_ifsc: string | null;
  upi_id: string | null;
  sac_code: string | null;
  payment_terms_days: number | null;
  created_at?: string;
  updated_at?: string;
}

export interface OrganizationListMeta {
  total: number;
  lastPage: number;
  currentPage: number;
  perPage: number;
  prev: number | null;
  next: number | null;
}

export interface OrganizationListPayload {
  result: OrganizationRecord[];
  meta: OrganizationListMeta;
}

export interface OrganizationListQuery {
  page?: number;
  page_size?: number;
  searchText?: string;
  sortColumn?: string;
  sortBy?: 'asc' | 'desc';
}

export interface CreateOrganizationBody {
  organization_name: string;
  legal_name: string;
  gst_no: string;
  pan_no: string;
  country_code: string;
  mobile_number: string;
  email: string;
  address: string;
  city: string;
  country: string;
  pincode: string;
  bank_name?: string;
  bank_account_name?: string;
  bank_account_number?: string;
  bank_ifsc?: string;
  upi_id?: string;
  sac_code?: string;
  payment_terms_days?: number;
}

export type UpdateOrganizationBody = Partial<CreateOrganizationBody>;

@Injectable({ providedIn: 'root' })
export class OrganizationApiService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiBaseUrl.replace(/\/$/, '');

  /** Public (no session): lets the sign-in page show the organization logo. */
  getPublicBranding(): Observable<ApiEnvelope<{ logo_url: string | null }>> {
    return this.http.get<ApiEnvelope<{ logo_url: string | null }>>(
      `${this.base}/public/branding`,
    );
  }

  getOrganization(): Observable<ApiEnvelope<OrganizationRecord | null>> {
    return this.http.get<ApiEnvelope<OrganizationRecord | null>>(
      `${this.base}/organization/me`,
    );
  }

  listOrganizations(
    query: OrganizationListQuery = {},
  ): Observable<ApiEnvelope<OrganizationListPayload>> {
    let params = new URLSearchParams();
    if (query.page != null) params.set('page', String(query.page));
    if (query.page_size != null) params.set('page_size', String(query.page_size));
    if (query.searchText?.trim()) params.set('searchText', query.searchText.trim());
    if (query.sortColumn) params.set('sortColumn', query.sortColumn);
    if (query.sortBy) params.set('sortBy', query.sortBy);
    const qs = params.toString();
    const url = qs
      ? `${this.base}/organization?${qs}`
      : `${this.base}/organization`;
    return this.http.get<ApiEnvelope<OrganizationListPayload>>(url);
  }

  createOrganization(
    body: CreateOrganizationBody,
  ): Observable<ApiEnvelope<OrganizationRecord>> {
    return this.http.post<ApiEnvelope<OrganizationRecord>>(
      `${this.base}/organization`,
      body,
    );
  }

  updateOrganization(
    id: number,
    body: UpdateOrganizationBody,
  ): Observable<ApiEnvelope<OrganizationRecord>> {
    return this.http.patch<ApiEnvelope<OrganizationRecord>>(
      `${this.base}/organization/${id}`,
      body,
    );
  }

  deleteOrganization(id: number): Observable<ApiEnvelope<null>> {
    return this.http.delete<ApiEnvelope<null>>(`${this.base}/organization/${id}`);
  }

  uploadLogo(
    id: number,
    file: File,
  ): Observable<ApiEnvelope<OrganizationRecord | null>> {
    const form = new FormData();
    form.append('logo', file);
    return this.http.post<ApiEnvelope<OrganizationRecord | null>>(
      `${this.base}/organization/${id}/logo`,
      form,
    );
  }

  removeLogo(id: number): Observable<ApiEnvelope<OrganizationRecord | null>> {
    return this.http.delete<ApiEnvelope<OrganizationRecord | null>>(
      `${this.base}/organization/${id}/logo`,
    );
  }
}
